import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { KatlasRecord, KatlasModeChip } from "../components/Katlas";
import { EvidenceMatrix } from "../components/EvidenceMatrix";
import { SATELLITES } from "../lib/satelliteConfig";
import { http } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldCheck, Wallet, UserRound, FileCheck2, Sparkles, Check, Lock } from "lucide-react";

/**
 * Single satellite scenario page. Routed at `/satellites/:kind`.
 *
 * One backbone, three branded satellites — page reads the scenario pack from
 * SATELLITES, calls the live KATLAS-signed `POST /api/satellites/:kind/approve`
 * endpoint, and renders the full receipt UX (KatlasRecord + CAR + evidence
 * visibility matrix) used elsewhere in ClubCierge.
 */
export default function SatelliteScenario() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const pack = SATELLITES[kind];
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pack) navigate("/", { replace: true });
  }, [pack, navigate]);

  useEffect(() => {
    if (!pack) return;
    let alive = true;
    http.get(`/satellites/${kind}/latest`).then((r) => {
      if (alive && r.data && r.data.id) setClaim(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [pack, kind]);

  if (!pack) return null;

  async function onApprove() {
    setLoading(true);
    try {
      const r = await http.post(`/satellites/${kind}/approve`, {});
      setClaim(r.data);
      const att = r.data?.katlas_attestation || {};
      if (att.mode === "live" && att.verification === "SUCCESS") {
        toast.success("KATLAS signed the satellite receipt.");
      } else {
        toast.message("Receipt recorded.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Approval failed";
      toast.error(typeof msg === "string" ? msg : "Approval failed");
    } finally {
      setLoading(false);
    }
  }

  const signed = claim?.katlas_attestation?.verification === "SUCCESS";

  return (
    <div className="space-y-8 max-w-6xl">
      {/* Back link */}
      <div className="text-[12px]">
        <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-clubcierge">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to ClubCierge
        </Link>
      </div>

      {/* Branded hero */}
      <section data-testid={`satellite-hero-${kind}`}>
        <Card
          className="race-card p-6 sm:p-8 relative overflow-hidden border-2"
          style={{ borderColor: `${pack.accent}66`, background: `linear-gradient(135deg, ${pack.accent}10 0%, #FFFFFF 60%)` }}
        >
          <div className="checker-stripe absolute -top-6 -right-6 w-40 h-40 opacity-40 pointer-events-none" />
          <div className="relative flex flex-wrap items-center gap-2">
            <span className="signal-bar" />
            <span className="tracking-eyebrow text-muted-foreground">Satellite club · {pack.brand}</span>
            <KatlasModeChip />
          </div>
          <h1 className="relative text-3xl sm:text-4xl font-semibold tracking-tight mt-2" style={{ color: pack.accent }}>
            {pack.workingName}
          </h1>
          <p className="relative text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{pack.heroTagline}</p>
          <div className="relative text-[12px] text-foreground/70 mt-3 italic">{pack.proofLine}</div>
        </Card>
      </section>

      {/* Named member / asset card */}
      <section className="grid sm:grid-cols-2 gap-4" data-testid="satellite-member-and-action">
        <Card className="p-5 border-border race-card">
          <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
            <Wallet className="h-3.5 w-3.5" /> Member / asset under custody
          </div>
          <div className="text-lg font-semibold mt-2">{pack.member.name}</div>
          <div className="text-[12px] text-muted-foreground">{pack.member.role}</div>
          <div className="text-[13px] mt-3 leading-relaxed">{pack.member.asset}</div>
        </Card>

        <Card className="p-5 border-border race-card">
          <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
            <UserRound className="h-3.5 w-3.5" /> Authority gate
          </div>
          <div className="text-lg font-semibold mt-2">{pack.authority.label}</div>
          <div className="text-[12px] text-muted-foreground">Acting officer: {pack.authority.person}</div>
          <div className="text-[13px] mt-3 leading-relaxed">{pack.action.summary}</div>
        </Card>
      </section>

      {/* Governed action — Approve button */}
      <section data-testid="satellite-governed-action">
        <Card className="p-5 border-border race-card space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Governed action
              </div>
              <div className="text-base font-semibold mt-1">{pack.action.label}</div>
              <div className="text-[12px] text-muted-foreground mt-1">
                Policy gate <span className="font-mono-ui">CAR-CLUBCIERGE-001</span>. KATLAS will sign the receipt on approval.
              </div>
            </div>
            <div className="flex gap-2">
              {claim?.id && (
                <span className="text-[11px] text-muted-foreground self-center font-mono-ui">{claim.id}</span>
              )}
              <Button onClick={onApprove} disabled={loading} data-testid="satellite-approve-btn">
                {loading ? "Signing…" : signed ? "Re-approve & sign" : `Approve as ${pack.authority.label}`}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </Card>
      </section>

      {/* Bounded AI line — satellites use AI on summaries only */}
      <section data-testid="satellite-bounded-ai">
        <Card className="p-4 border-[#F25E1D]/30 bg-[#FFF7F2] race-card grid sm:grid-cols-2 gap-3 text-[12px]">
          <div>
            <div className="tracking-eyebrow text-[#1F4A2D] inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> AI may access
            </div>
            <ul className="mt-1.5 space-y-1 text-foreground/80">
              {pack.shared_fields.map((f) => (<li key={f}>· {f}</li>))}
            </ul>
          </div>
          <div>
            <div className="tracking-eyebrow text-[#7A2E16] inline-flex items-center gap-1">
              <Lock className="h-3 w-3" /> AI must not access
            </div>
            <ul className="mt-1.5 space-y-1 text-foreground/80">
              {pack.withheld_fields.map((f) => (<li key={f}>· {f}</li>))}
            </ul>
          </div>
          <div className="sm:col-span-2 text-[11px] italic text-muted-foreground border-t border-[#F25E1D]/20 pt-2">
            <Sparkles className="h-3 w-3 inline -mt-0.5 mr-1" />
            AI drafts from approved evidence only. It cannot approve the action or access withheld profile data.
            {pack.boundary && <div className="mt-1 not-italic font-medium text-foreground/80">{pack.boundary}</div>}
          </div>
        </Card>
      </section>

      {/* Live KATLAS receipt */}
      {claim?.id && (
        <section data-testid="satellite-receipt">
          <Card className="p-6 border-border race-card space-y-4">
            <div>
              <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1">
                <FileCheck2 className="h-3.5 w-3.5" /> Receipt
              </div>
              <h2 className="text-xl font-semibold mt-1">{pack.action.label}</h2>
              <div className="text-[12px] text-muted-foreground mt-1">
                Approved {claim.recorded_at} by {pack.authority.person} ({pack.authority.label})
              </div>
            </div>

            <KatlasRecord
              hash={claim.hash}
              receiptId={claim.id}
              policyGate="passed"
              actor={`${pack.authority.person} (${pack.authority.label})`}
              action={pack.action.label}
              shared={pack.shared_fields}
              withheld={pack.withheld_fields}
              attestation={claim.katlas_attestation || null}
              timestamp={claim.recorded_at || null}
              car={{
                custody: pack.car.custody,
                authority: pack.car.authority,
                receipt: claim.katlas_attestation?.mode === "live"
                  ? `KATLAS signed the approval at ${claim.katlas_attestation.node_timestamp || claim.recorded_at}, producing receipt hash ${claim.katlas_attestation.attestation_hash || claim.hash}.`
                  : `${pack.car.receipt} (governance simulation in this environment)`,
              }}
            />
          </Card>
        </section>
      )}

      {/* Evidence visibility matrix — scenario-specific */}
      <EvidenceMatrix
        title="Evidence visibility matrix"
        subtitle={`Same KATLAS data-boundary rules applied to the ${pack.brand.toLowerCase()} scenario.`}
        headers={[
          "Evidence / fact",
          "Controlled by",
          "Visible to counterparty",
          "Visible to authorising officer",
          "Sent to KATLAS node",
          "Public share",
        ]}
        rows={[
          ...pack.shared_fields.map((f) => [f, pack.member.role, "If approved", "Yes", "Hash / ref only", "Ref / hash only"]),
          ...pack.withheld_fields.map((f) => [f, pack.member.role, "No", "Restricted only", "No", "No"]),
        ]}
        testId={`satellite-matrix-${kind}`}
      />

      {/* Footer link back */}
      <div className="text-[12px] text-muted-foreground italic">
        Same backbone as ClubCierge — one live KATLAS integration, three branded satellite pathways.
        <Link to="/" className="ml-1 underline hover:text-foreground">Return to the main ClubCierge proof path</Link>.
      </div>
    </div>
  );
}
